<template>
    <ChannelCredentialsPanelCmp
        :channel="channel"
        :diagram-state="diagramState"
        :diagram-failed="!!lastTest && !lastTest.ok"
        :title="isConfigured ? `Credenciais do ${channelName}` : `Conecte o ${channelName} ao Muven`"
        :lead="isConfigured
            ? `Para trocar, cole as novas chaves geradas no painel do ${channelName}. Antes de salvar, o Muven testa a conexão.`
            : `Cole as chaves de API geradas no painel do ${channelName}. Antes de salvar, o Muven testa a conexão.`"
        :tutorial-link="linkTutorialCredentials"
        :tutorial-description="`Passo a passo para gerar as credenciais no ${channelName}.`">

        <form :id="formId" novalidate @submit.prevent="testAndSave(credentialsForm, () => saveCredentials(credentialsForm))">
            <fieldset class="cred-fields" :disabled="isBusy">
                <legend class="sr-only">Credenciais {{channelName}}</legend>

                <CredentialFieldCmp
                    v-for="(field, key) in credentialsForm"
                    :key="key"
                    :ref="`field-${key}`"
                    :field-key="key"
                    :field="field"
                    :channel-enum="channel.enumChannel"
                    :submitted="submitted" />
            </fieldset>

            <ChannelCredentialsTestResultCmp
                ref="testResult"
                :last-test="lastTest"
                :saving="savingContent"
                save-anyway
                @review="focusFirstField()"
                @save-anyway="saveCredentials(credentialsForm)" />

            <WooCommerceIpBlockNoticeCmp
                class="cred-ip-notice"
                v-if="channel.isWooCommerce()"
                :blocked="ipBlockSuspected"
                :store-url="storeUrl" />

            <p class="cred-golive">
                <fa icon="info-circle" class="cred-golive-icon" aria-hidden="true" />
                <span>Ao salvar, o Muven começa a sincronizar com a loja. Salve quando produtos, categorias e marcas estiverem prontos para a integração.</span>
            </p>
        </form>

        <template v-slot:footer-start>
            <router-link :to="`/canais/${channel.id}/configuracoes`" class="btn btn-outline-secondary">
                <fa icon="arrow-left" />
                Voltar
            </router-link>

            <span class="cred-dirty" v-if="isDirty" role="status">
                <span class="cred-dirty-dot" aria-hidden="true"></span>
                Alterações não salvas
            </span>
        </template>

        <template v-slot:footer-end>
            <button type="button"
                class="btn btn-outline-secondary"
                :disabled="isBusy"
                @click="testConnection(credentialsForm)">
                <fa icon="sync-alt" :spin="testingConnection" />
                {{testingConnection ? 'Testando conexão' : 'Testar conexão'}}
            </button>

            <button type="submit"
                class="btn btn-primary"
                :form="formId"
                :disabled="isBusy">
                <fa icon="sync-alt" spin v-if="isBusy" />
                <fa icon="check" v-else />
                {{primaryLabel}}
            </button>
        </template>
    </ChannelCredentialsPanelCmp>
</template>

<script>
import { ChannelModel } from '@/channel/models/ChannelModel'
import channelService from '@/channel/services/ChannelService'
import dynamicCredentialsDataParser from '@/channel/utils/DynamicCredentialsDataParser'
import WooCommerceIpBlockNoticeCmp from '@/channel/components/WooCommerceIpBlockNoticeCmp'
import ChannelCredentialsPanelCmp from '@/channel/components/ChannelCredentialsPanelCmp.vue'
import ChannelCredentialsTestResultCmp from '@/channel/components/ChannelCredentialsTestResultCmp.vue'
import CredentialFieldCmp from '@/channel/components/CredentialFieldCmp.vue'
import CredentialsTestAndSave from '@/channel/mixins/CredentialsTestAndSave'

export default {
    name: 'ChannelCredentialsSimple',
    components: {
        WooCommerceIpBlockNoticeCmp,
        ChannelCredentialsPanelCmp,
        ChannelCredentialsTestResultCmp,
        CredentialFieldCmp,
    },
    mixins: [
        CredentialsTestAndSave,
    ],
    props: {
        channel: {
            required: true,
            type: ChannelModel,
            default: new ChannelModel(),
        },
        credentialsForm: {
            required: true,
            type: Object,
            default: Object.create(null),
        },
        linkTutorialCredentials: {
            required: true,
            type: String,
            default: '',
        },
    },
    computed: {
        storeUrl() {
            const urlField = this.credentialsForm.url
            return urlField && typeof urlField.value === 'string' ? urlField.value : ''
        },
        channelName() {
            return this.channel.getRealName()
        },
        formId() {
            return `cred-form-${this._uid}`
        },
        primaryLabel() {
            if (this.testingConnection) {
                return 'Testando conexão'
            }

            return this.savingContent ? 'Salvando' : 'Testar e salvar'
        },
    },
    methods: {
        async saveCredentials(credentialsForm) {
            this.$floatingAlert.hide()

            this.savingContent = true

            const channelId = this.$route.params.id
            const credentials = dynamicCredentialsDataParser.mountCredentialsObjectFromForm(credentialsForm)

            try {
                await channelService.saveCredentials(channelId, credentials)

                this.$floatingAlert.success({
                    message: 'Credenciais salvas com sucesso!',
                })

                this.markSaved()
                this.$router.push(`/canais/${channelId}/configuracoes`)
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.savingContent = false
        },
        // Só testa; o resultado fica na tela
        async testConnection(credentialsForm) {
            await this.runTest(credentialsForm)
        },
    },
}
</script>

<style lang="scss" scoped>
.cred-fields {
    display: flex;
    flex-direction: column;
    gap: 20px;
    min-width: 0;
    margin: 0;
    padding: 0;
    border: 0;
}

.cred-ip-notice {
    margin-top: 20px;
}

.cred-golive {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 24px 0 0;
    padding-top: 18px;
    border-top: 1px solid var(--border-subtle);
    color: var(--text-muted);
    font-size: 13px;
    line-height: 1.5;
}

.cred-golive-icon {
    flex-shrink: 0;
    margin-top: 3px;
    color: var(--text-faint);
}

.cred-dirty {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--warning-fg);
    font-size: 13px;
    font-weight: 700;
}

.cred-dirty-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--warning-solid);
}
</style>
